import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { Layout } from './presentation/components/Layout';
import { WelcomePage } from './presentation/pages/WelcomePage';
import { LoginPage } from './presentation/pages/LoginPage';
import { CreateUserPage } from './presentation/pages/CreateUserPage';
import ProcessesPage from './presentation/pages/ProcessesPage';
import ProcessInstancePage from './presentation/pages/ProcessInstancePage';
import AnalyticsPage from './presentation/pages/AnalyticsPage';
import TasksPage from './presentation/pages/TasksPage';
import IncidentsPage from './presentation/pages/IncidentsPage';
import UsersPage from './presentation/pages/IAMPage';
import StoragePage from './presentation/pages/StoragePage';
import IntegrationsPage from './presentation/pages/IntegrationsPage';
import SettingsPage from './presentation/pages/SettingsPage';
import ToolsPage from './presentation/pages/ToolsPage';
import StudioPage from './presentation/pages/StudioPage';
import DecisionDesignerPage from './presentation/pages/DecisionDesignerPage';
import RuleFormPage from './presentation/pages/RuleFormPage';
import OcrTrackingPage from './presentation/pages/OcrTrackingPage';
import TemplateEnginePage from './presentation/pages/TemplateEnginePage';
import VerificationCockpitPage from './presentation/pages/VerificationCockpitPage';
import FinalCheckerPage from './presentation/pages/FinalCheckerPage';
import BusinessRulesPage from './presentation/components/documents/BusinessRulesPage';
import AppAuthProvider, { useAuthContext } from './components/auth/AuthProvider';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ProtectedPage from './components/auth/ProtectedPage';
import RBACProvider from './components/auth/RBACContext';
import Callback from './components/auth/Callback';
import { AuthToast } from './components/auth/AuthToast';
import { setupDependencyInjection } from './shared/di/DependencyInjection';
import Sidebar from './presentation/components/ui/Sidebar';
import { useResolveOcrAccount } from './presentation/hooks/useResolveOcrAccount';

declare global {
  interface Window {
    __DOIT_OCR_CONFIG__?: { app_name: string; tracking_table: string };
    __DOIT_HOSTED__?: boolean;
  }
}
window.__DOIT_OCR_CONFIG__ = {
  app_name: (import.meta.env.VITE_OCR_APP_NAME as string) ?? 'Document Extraction',
  tracking_table: (import.meta.env.VITE_OCR_TRACKING_TABLE as string) ?? 'doc_extraction_tracking',
};

const DocExtractionRemote = React.lazy(() => import('docExtraction/App'));

class RemoteErrorBoundary extends React.Component<
  { children: React.ReactNode }, { error: Error | null }
> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[doc-extraction MFE] failed to load:', error, info);
  }
  render() {
    if (this.state.error) {
      return <div className="p-8 text-red-700">Document-extraction failed to load.</div>;
    }
    return this.props.children;
  }
}

// CRITICAL: snapshot Doit's <head> ONCE at first OCR mount. Reuse forever.
let doitHeadSnapshot: Set<Node> | null = null;

function DocExtractionStyleIsolation({ children }: { children: React.ReactNode }) {
  React.useEffect(() => {
    if (doitHeadSnapshot === null) {
      doitHeadSnapshot = new Set<Node>(Array.from(document.head.children));
    }
    document.querySelectorAll<HTMLStyleElement | HTMLLinkElement>(
      '[data-doit-mfe-disabled]'
    ).forEach(el => {
      el.disabled = false;
      el.removeAttribute('data-doit-mfe-disabled');
    });
    return () => {
      const snap = doitHeadSnapshot!;
      Array.from(document.head.children).forEach(child => {
        if (snap.has(child)) return;
        const el = child as HTMLElement;
        if (el.tagName === 'STYLE') {
          (el as HTMLStyleElement).disabled = true;
          el.setAttribute('data-doit-mfe-disabled', 'true');
        } else if (el.tagName === 'LINK' && (el as HTMLLinkElement).rel === 'stylesheet') {
          (el as HTMLLinkElement).disabled = true;
          el.setAttribute('data-doit-mfe-disabled', 'true');
        }
      });
    };
  }, []);
  return <>{children}</>;
}

function DocExtractionAuthBridge({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuthContext();
  // The OCR backend keys identity off a canonical user_id resolved from
  // (orgId, preferred_username). The remote BaseClient reads it as
  // X-User-Id from sessionStorage.user.user_id, so we must wait for the
  // /document_extraction/account resolve to complete BEFORE mounting
  // the remote. profile.sub is NOT accepted.
  const { xUserId } = useResolveOcrAccount();

  window.__DOIT_HOSTED__ = true;

  if (!isAuthenticated || !user?.access_token || !xUserId) {
    return (
      <div className="flex h-full w-full items-center justify-center text-sm text-gray-500">
        Preparing document extraction…
      </div>
    );
  }

  window.sessionStorage.setItem('token', user.access_token);
  const profile = user.profile ?? {};
  window.sessionStorage.setItem('user', JSON.stringify({
    user_id: xUserId,
    email: profile.email,
    name: profile.name,
    jwt_token: user.access_token,
    access_token: user.access_token,
  }));

  return <>{children}</>;
}

// Create a client
const queryClient = new QueryClient();

setupDependencyInjection();

/**
 * Backwards-compat redirect: the old OCR routes lived under /documents/*.
 * They've moved to /ocr-tracking/*, but cached browser history and
 * bookmarks still resolve the old path. Preserve the query string so
 * ?id=… deep-links keep working.
 */
function LegacyDocumentsRedirect({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

/**
 * Wrap a route element with both the login guard (`ProtectedRoute`) and
 * the RBAC guard (`ProtectedPage`). Centralising this keeps the route
 * table readable and ensures we never forget one of the two.
 *
 * `page` must match a key in PAGE_ACCESS (see RBACContext).
 */
function Guarded({ page, children }: { page: string; children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <ProtectedPage page={page}>{children}</ProtectedPage>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppAuthProvider>
        {/* RBACProvider sits inside AppAuthProvider because it reads
            user/token from useAuthContext. Everything that uses
            useRBAC() must descend from this provider. */}
        <RBACProvider>
          <Router basename={import.meta.env.BASE_URL}>
            <AuthToast />
            <Toaster
              position="bottom-center"
              richColors
              closeButton
              expand
              visibleToasts={3}
              gap={10}
              theme="light"
              toastOptions={{
                style: {
                  borderRadius: "10px",
                  fontSize: "13px",
                  padding: "12px 14px",
                  boxShadow:
                    "0 8px 24px -8px rgba(15, 23, 42, 0.18), 0 2px 6px -2px rgba(15, 23, 42, 0.08)",
                },
              }}
            />
            <Routes>
              {/* Authentication Routes
                  Note: `/silent-renew` is intentionally NOT a React route —
                  it is handled in `main.tsx` BEFORE the React app mounts so
                  the silent-renew iframe doesn't accidentally boot a second
                  AuthProvider tree. */}
              <Route path="/callback" element={<Callback />} />

              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/" element={<WelcomePage />} />

              {/* Protected Routes — each Guarded() pairs a login check
                  with a PAGE_ACCESS lookup so an unauthorised user who
                  pastes a URL gets the AccessDenied screen instead of
                  the page itself. */}
              <Route
                path="/home"
                element={
                  <ProtectedRoute>
                    <Layout><ProcessesPage /></Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users/create"
                element={
                  <Guarded page="iam">
                    <Layout><CreateUserPage /></Layout>
                  </Guarded>
                }
              />
              <Route
                path="/processes"
                element={<Guarded page="processes"><ProcessesPage /></Guarded>}
              />
              <Route
                path="/processes/instance/:instanceId"
                element={<Guarded page="processes"><ProcessInstancePage /></Guarded>}
              />
              <Route
                path="/analytics"
                element={<Guarded page="analytics"><AnalyticsPage /></Guarded>}
              />
              <Route
                path="/tasks"
                element={<Guarded page="tasks"><TasksPage /></Guarded>}
              />
              <Route
                path="/incidents"
                element={<Guarded page="incidents"><IncidentsPage /></Guarded>}
              />
              <Route
                path="/iam"
                element={<Guarded page="iam"><UsersPage /></Guarded>}
              />
              <Route
                path="/storage"
                element={<Guarded page="storage"><StoragePage /></Guarded>}
              />
              <Route
                path="/integrations"
                element={<Guarded page="integrations"><IntegrationsPage /></Guarded>}
              />
              <Route
                path="/settings"
                element={<Guarded page="settings"><SettingsPage /></Guarded>}
              />
              <Route
                path="/studio"
                element={<Guarded page="studio"><StudioPage /></Guarded>}
              />
              <Route
                path="/studio/connectors/new"
                element={<Guarded page="studio"><StudioPage /></Guarded>}
              />
              <Route
                path="/studio/connectors/:connectorId"
                element={<Guarded page="studio"><StudioPage /></Guarded>}
              />
              <Route
                path="/studio/forms/:formSlug"
                element={<Guarded page="studio"><StudioPage /></Guarded>}
              />
              <Route
                path="/studio/decisions/:decisionSlug"
                element={<Guarded page="studio"><DecisionDesignerPage /></Guarded>}
              />
              <Route
                path="/studio/rules/new"
                element={<Guarded page="studio"><RuleFormPage /></Guarded>}
              />
              <Route
                path="/studio/rules/:ruleId/edit"
                element={<Guarded page="studio"><RuleFormPage /></Guarded>}
              />
              {/* OCR Template Engine — verify / final-checker / business-rules
                  must come BEFORE the bare `/studio/templateEngine` and
                  `/studio/:workflowSlug` so a sub-segment path doesn't match
                  the index / catch-all first. These reuse the same cockpit
                  / final-checker / business-rules components as the OCR
                  Tracking flow, but at a distinct URL so back-navigation
                  routes the user back to the Template Engine inbox. */}
              <Route
                path="/studio/templateEngine/verify"
                element={<Guarded page="template-engine"><VerificationCockpitPage /></Guarded>}
              />
              <Route
                path="/studio/templateEngine/final-checker"
                element={<Guarded page="template-engine"><FinalCheckerPage /></Guarded>}
              />
              <Route
                path="/studio/templateEngine/business-rules"
                element={<Guarded page="template-engine"><BusinessRulesPage /></Guarded>}
              />
              <Route
                path="/studio/templateEngine"
                element={<Guarded page="template-engine"><TemplateEnginePage /></Guarded>}
              />
              <Route
                path="/studio/:workflowSlug"
                element={<Guarded page="studio"><StudioPage /></Guarded>}
              />
              <Route
                path="/tools/*"
                element={<Guarded page="tools"><ToolsPage /></Guarded>}
              />

              {/* OCR Tracking — verify/business-rules listed BEFORE the index
                  so they match first when the path has a sub-segment. */}
              <Route
                path="/ocr-tracking/verify"
                element={<Guarded page="ocr-tracking"><VerificationCockpitPage /></Guarded>}
              />
              <Route
                path="/ocr-tracking/final-checker"
                element={<Guarded page="ocr-tracking"><FinalCheckerPage /></Guarded>}
              />
              <Route
                path="/ocr-tracking/business-rules"
                element={<Guarded page="ocr-tracking"><BusinessRulesPage /></Guarded>}
              />
              <Route
                path="/ocr-tracking"
                element={<Guarded page="ocr-tracking"><OcrTrackingPage /></Guarded>}
              />

              {/* Federated Document-Extraction microfrontend.
                  Host gates auth via <Guarded>, then bridges the OIDC token
                  into sessionStorage for the remote BaseClient. Sidebar is
                  rendered as a sibling so it stays visible. */}
              <Route
                path="/document-extraction/*"
                element={
                  <Guarded page="ocr-tracking">
                    <>
                      <Sidebar />
                      <div className="fixed top-0 left-16 right-0 bottom-0 overflow-hidden bg-white">
                        <DocExtractionStyleIsolation>
                          <DocExtractionAuthBridge>
                            <RemoteErrorBoundary>
                              <Suspense fallback={
                                <div className="flex h-full w-full items-center justify-center text-sm text-gray-500">
                                  Loading document extraction…
                                </div>
                              }>
                                <DocExtractionRemote />
                              </Suspense>
                            </RemoteErrorBoundary>
                          </DocExtractionAuthBridge>
                        </DocExtractionStyleIsolation>
                      </div>
                    </>
                  </Guarded>
                }
              />

              {/* Legacy /documents/* paths — redirect to the new locations. */}
              <Route
                path="/documents/verify"
                element={<LegacyDocumentsRedirect to="/ocr-tracking/verify" />}
              />
              <Route
                path="/documents"
                element={<LegacyDocumentsRedirect to="/ocr-tracking" />}
              />
            </Routes>
          </Router>
        </RBACProvider>
      </AppAuthProvider>
    </QueryClientProvider>
  );
}

export default App;
